import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { MessageCircle, X, Send, Sparkles, CalendarDays, Check } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/lib/i18n";
import api, { apiError } from "@/lib/api";

function getSessionId(slug) {
  const key = `shialoc-chat-${slug}`;
  let id = sessionStorage.getItem(key);
  if (!id) {
    id = `${slug}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    sessionStorage.setItem(key, id);
  }
  return id;
}

export const ChatWidget = ({ slug }) => {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [registered, setRegistered] = useState({});
  const bottomRef = useRef(null);
  const locale = lang === "fr" ? "fr-FR" : "en-US";

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading, open]);

  const send = async (text) => {
    const msg = (text || input).trim();
    if (!msg || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setLoading(true);
    try {
      const { data } = await api.post(`/associations/${slug}/chatbot`, {
        message: msg,
        session_id: getSessionId(slug),
      });
      setMessages((m) => [...m, { role: "bot", content: data.reply, events: data.events || [] }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "bot", content: t("chatbot_error"), error: true }]);
    } finally {
      setLoading(false);
    }
  };

  const register = async (eventId) => {
    if (!user) {
      toast.error(t("chatbot_login_to_register"));
      navigate("/login");
      return;
    }
    try {
      const { data } = await api.post(`/associations/${slug}/events/${eventId}/register`);
      setRegistered((r) => ({ ...r, [eventId]: data.registered }));
      toast.success(data.registered ? t("chatbot_register_success") : t("chatbot_unregister_success"));
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const fmtDate = (iso) => {
    const d = new Date(iso);
    return `${d.toLocaleDateString(locale, { weekday: "short", day: "numeric", month: "short" })} · ${d.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" })}`;
  };

  const suggestions = [t("chatbot_sugg_1"), t("chatbot_sugg_2"), t("chatbot_sugg_3")];

  return createPortal(
    <>
      <button
        data-testid="chatbot-toggle-btn"
        onClick={() => setOpen((o) => !o)}
        aria-label={t("chatbot_title")}
        className="fixed bottom-5 right-5 z-50 h-12 w-12 p-3 rounded-full bg-accent text-accent-foreground shadow-lg hover:scale-105 active:scale-95 transition-transform"
      >
        {open ? <X className="h-6 w-6" /> : <MessageCircle className="h-6 w-6" />}
      </button>

      {open && (
        <div
          data-testid="chatbot-panel"
          className="fixed bottom-24 right-5 z-50 w-[360px] max-w-[calc(100vw-2.5rem)] h-[480px] max-h-[70vh] flex flex-col rounded-xl border border-border bg-card shadow-2xl overflow-hidden"
        >
          <div className="flex items-center gap-2 px-4 py-3 border-b border-border bg-accent/5">
            <Sparkles className="h-4 w-4 text-accent" />
            <p className="font-heading font-bold text-sm flex-1">{t("chatbot_title")}</p>
            <button data-testid="chatbot-close-btn" onClick={() => setOpen(false)} className="text-muted-foreground hover:text-foreground">
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
            {messages.length === 0 && (
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">{t("chatbot_intro")}</p>
                <div className="flex flex-col gap-2">
                  {suggestions.map((s) => (
                    <button
                      key={s}
                      data-testid="chatbot-suggestion"
                      onClick={() => send(s)}
                      className="text-left text-xs px-3 py-2 rounded-lg border border-border hover:border-accent/50 hover:bg-accent/5 transition-colors"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex flex-col ${m.role === "user" ? "items-end" : "items-start"}`}>
                <div
                  data-testid={m.role === "user" ? "chatbot-message-user" : "chatbot-message-bot"}
                  className={`max-w-[85%] px-3 py-2 rounded-xl text-sm whitespace-pre-wrap leading-relaxed ${
                    m.role === "user"
                      ? "bg-accent text-accent-foreground rounded-br-sm"
                      : m.error
                      ? "bg-destructive/10 text-destructive rounded-bl-sm"
                      : "bg-muted rounded-bl-sm"
                  }`}
                >
                  {m.content}
                </div>
                {m.events?.length > 0 && (
                  <div className="mt-2 space-y-2 w-full max-w-[92%]">
                    {m.events.map((ev) => (
                      <div key={ev.id} data-testid="chatbot-event-card" className="border border-border rounded-lg p-2.5 bg-card">
                        <p className="font-heading font-semibold text-xs truncate">{ev.title}</p>
                        <p className="text-[11px] font-mono-data text-muted-foreground mt-0.5 flex items-center gap-1">
                          <CalendarDays className="h-3 w-3 shrink-0" />
                          {fmtDate(ev.start)}{ev.mosque_name ? ` — ${ev.mosque_name}` : ""}
                        </p>
                        <div className="flex gap-1.5 mt-2">
                          <Button
                            size="sm"
                            variant="outline"
                            data-testid="chatbot-view-event-btn"
                            onClick={() => navigate(`/${slug}/events/${ev.id}`)}
                            className="rounded-full h-6 text-[11px] px-3"
                          >
                            {t("chatbot_view_event")}
                          </Button>
                          <Button
                            size="sm"
                            variant={registered[ev.id] ? "secondary" : "default"}
                            data-testid="chatbot-register-btn"
                            onClick={() => register(ev.id)}
                            className="rounded-full h-6 text-[11px] px-3 active:scale-95"
                          >
                            {registered[ev.id] ? <><Check className="h-3 w-3 mr-1" />{t("chatbot_registered")}</> : t("chatbot_register")}
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
            {loading && (
              <div className="flex justify-start" data-testid="chatbot-loading">
                <div className="bg-muted px-3 py-2 rounded-xl rounded-bl-sm text-sm text-muted-foreground animate-pulse">
                  …
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          <div className="flex gap-2 p-3 border-t border-border">
            <Input
              data-testid="chatbot-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder={t("chatbot_placeholder")}
              className="h-9 text-sm"
            />
            <Button data-testid="chatbot-send-btn" size="icon" onClick={() => send()} disabled={loading || !input.trim()} className="h-9 w-9 rounded-full shrink-0 active:scale-95">
              <Send className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </>,
    document.body
  );
};
